/* eslint-disable new-cap */
/**
 * REST API を使用したメールテンプレート管理 hooks
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { restClient } from "../lib/rest-client";

// =====================================
// Types
// =====================================

export const EmailTemplateCategory = {
  Hr: "HR",
  Matter: "MATTER",
  All: "ALL",
} as const;

export type EmailTemplateCategory =
  (typeof EmailTemplateCategory)[keyof typeof EmailTemplateCategory];

export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  from: string;
  category: EmailTemplateCategory;
  createdBy: string;
  createdAt: string;
}

export interface EmailTemplateDocuments {
  items: EmailTemplate[];
  total: number;
}

export interface CreateEmailTemplateInput {
  name: string;
  subject: string;
  body: string;
  from: string;
  category: EmailTemplateCategory;
}

export interface EmailTemplateSearchInput {
  category?: EmailTemplateCategory;
  from?: string;
  name?: string;
}

// =====================================
// useEmailTemplatesRestApi - メールテンプレート一覧取得
// =====================================

export interface UseEmailTemplatesRestApiOptions {
  filter?: EmailTemplateSearchInput;
  skip?: boolean;
  onCompleted?: () => void;
}

export interface UseEmailTemplatesRestApiResult {
  data: { emailTemplates: EmailTemplateDocuments } | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<EmailTemplateDocuments>;
}

/**
 * メールテンプレート一覧を取得するhook
 * @param {UseEmailTemplatesRestApiOptions} options - オプション設定
 * @return {UseEmailTemplatesRestApiResult} テンプレート一覧、エラー、ローディング状態、refetch関数
 */
export function useEmailTemplatesRestApi(
  options: UseEmailTemplatesRestApiOptions = {},
): UseEmailTemplatesRestApiResult {
  const [data, setData] = useState<
    { emailTemplates: EmailTemplateDocuments } | undefined
  >(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(!options.skip);

  const fetchEmailTemplates =
    useCallback(async (): Promise<EmailTemplateDocuments> => {
      setLoading(true);
      setError(undefined);

      try {
        const params: Record<string, string> = {};
        if (options.filter) {
          if (options.filter.category) {
            params.category = options.filter.category;
          }
          if (options.filter.name) {
            params.name = options.filter.name;
          }
          if (options.filter.from) {
            params.from = options.filter.from;
          }
        }

        const responseData = await restClient.get<EmailTemplateDocuments>(
          "/emails/templates",
          Object.keys(params).length > 0 ? params : undefined,
        );

        setData({ emailTemplates: responseData });
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
        options.onCompleted?.();
      }
    }, [options.filter, options.onCompleted]);

  const initialFetchDone = useRef(false);
  const skipRef = useRef(options.skip);

  useEffect(() => {
    if (!skipRef.current && !initialFetchDone.current) {
      initialFetchDone.current = true;
      fetchEmailTemplates().catch(() => {
        // エラーは state で管理
      });
    }
  }, [fetchEmailTemplates]);

  return {
    data,
    error,
    loading,
    refetch: fetchEmailTemplates,
  };
}

// =====================================
// useCreateEmailTemplateRestApi - メールテンプレート作成
// =====================================

export interface UseCreateEmailTemplateRestApiResult {
  createEmailTemplate: (
    input: CreateEmailTemplateInput,
  ) => Promise<EmailTemplate>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * メールテンプレートを作成するhook
 * @return {UseCreateEmailTemplateRestApiResult} 作成関数、ローディング状態、エラー
 */
export function useCreateEmailTemplateRestApi(): UseCreateEmailTemplateRestApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const createEmailTemplate = useCallback(
    async (input: CreateEmailTemplateInput): Promise<EmailTemplate> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData = await restClient.post<EmailTemplate>(
          "/emails/templates",
          input,
        );
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    createEmailTemplate,
    loading,
    error,
  };
}

export default {
  useEmailTemplatesRestApi,
  useCreateEmailTemplateRestApi,
};
